import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import DonateButton from "@/components/DonateButton";
import { Link } from "react-router-dom";

const LINES = [
  { text: "CHANGE", cls: "font-display font-semibold tracking-tight text-ivory" },
  { text: "FOR CONGO", cls: "font-display italic font-medium tracking-tight text-gold" },
];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.02, 1.14]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} className="relative flex min-h-[100svh] items-end overflow-hidden bg-charcoal" data-testid="kinetic-hero-section">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1761998849345-7757a8c3b818?auto=format&fit=crop&w=2400&q=80"
          alt="River winding through the green Congo Basin rainforest"
          className="h-full w-full object-cover"
          fetchpriority="high"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-charcoal/85 via-charcoal/25 to-charcoal/40" />
      <div className="grain absolute inset-0" />

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-24 pt-40 lg:px-8 lg:pb-28">
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="eyebrow mb-6 flex items-center gap-3 text-ivory/85"
          data-testid="hero-eyebrow"
        >
          <span className="inline-block h-px w-10 bg-gold" />
          Nseya Foundation · Humanitarian Foundation
        </motion.p>

        <h1 className="text-[clamp(3rem,9vw,7.5rem)] leading-[0.98]">
          {LINES.map((line, i) => (
            <span key={line.text} className="mask-line">
              <motion.span
                className={`block ${line.cls}`}
                initial={{ y: "112%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, delay: 0.25 + i * 0.14, ease: [0.25, 1, 0.5, 1] }}
                data-testid={`hero-line-${i + 1}`}
              >
                {line.text}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7 }}
          className="mt-7 max-w-xl font-display text-xl italic text-ivory sm:text-2xl"
          data-testid="hero-subline"
        >
          Congo gives so much to the world.
        </motion.p>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.82 }}
          className="mt-4 max-w-xl text-base leading-relaxed text-ivory/80 sm:text-lg"
          data-testid="hero-copy"
        >
          We believe the people of Congo deserve a future built on dignity, safety, opportunity and access to the resources every community needs to thrive.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.95 }}
          className="mt-10 flex flex-wrap items-center gap-4"
          data-testid="hero-cta-group"
        >
          <DonateButton variant="gold" testid="hero-donate-button" />
          <Link
            to="/programs"
            data-testid="hero-explore-work-button"
            className="inline-flex items-center justify-center gap-2 rounded-full border border-ivory/70 px-7 py-3.5 text-sm font-semibold uppercase tracking-[0.18em] text-ivory transition-all duration-300 hover:bg-ivory hover:text-forest"
          >
            Explore Our Work
          </Link>
        </motion.div>
      </motion.div>

      <motion.div
        style={{ opacity: fade }}
        className="absolute bottom-8 right-6 z-10 hidden items-center gap-2 text-ivory/60 lg:flex"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        aria-hidden="true"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.3em]">Scroll</span>
        <ArrowDown className="h-4 w-4 animate-float-slow" />
      </motion.div>
    </section>
  );
}
